import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, useRoute, useRouter, useStore, watch } from "@nuxtjs/composition-api";
import { onClickOutside, watchDebounced } from "@vueuse/core";

import { useTranslate } from "@/composables";
import { useRotatingSearchSuggestions } from "@/composables/search";
import { useConsumerSearchStore } from "@/store";
import { getAmplitudeStore } from "@/store/amplitude";
import { getSearchStore } from "@/store/search";
import { useSearchAssistanceStore } from "@/store/search-assistance";
import { SEARCH_SUGGESTIONS_DEBOUNCE_DELAY, SEARCH_SUGGESTIONS_LIMIT } from "@/utils/constants";


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchInput',
  setup(__props, { expose }) {

const { t } = useTranslate();
const router = useRouter();
const route = useRoute();
const store = useStore();
const searchStore = getSearchStore(store);
const searchAssistanceStore = useSearchAssistanceStore();
const consumerSearchStore = useConsumerSearchStore();

const { currentSuggestion, searchSuggestions } = useRotatingSearchSuggestions();

const contentRef = ref<HTMLDivElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const inputValue = ref("");
const menuModel = ref(false);
const menuItemIndex = ref(-1);

const getSearchHistory = (type: SearchType) => getSearchStore(store).historyTypeItem(type);

const placeholderText = computed(() => {
  return currentSuggestion.value;
});

const recentItems = computed(() => {
  const items = [];
  const recentSearches = getSearchHistory("post");
  if (recentSearches.length) {
    items.push({ header: t("Recent Searches").value, items: recentSearches });
  }
  const recentCreators = getSearchHistory("profile");
  if (recentCreators.length) {
    items.push({ header: t("Recent Creators").value, items: recentCreators });
  }
  return items;
});

const popularItems = computed(() => {
  const items = [];
  const popularSearches = searchSuggestions.value.map((suggestion) => ({
    objectID: suggestion,
    query: suggestion,
  }));
  if (searchSuggestions.value.length) {
    items.push({ header: t("Popular Searches").value, items: popularSearches });
  }
  return items;
});

const searchItems = computed(() => {
  const items = [];
  const autocompleteResults = searchAssistanceStore.autocompleteResults.map((result) => {
    if (result.type === "creator") {
      return {
        objectID: result.id,
        query: result.name,
        avatarUrl: result.imageUrl ? `${result.imageUrl}?v=0&format=auto&w=100&h=100&fit=crop&q=80` : "",
      };
    }
    return {
      objectID: result.name,
      query: result.name,
    };
  });
  if (autocompleteResults.length) {
    items.push({ header: t("Related Searches").value, items: autocompleteResults });
  }
  return items;
});

const sections = computed<Array<{ header: string; items: SearchSuggestion[] }>>(() => {
  if (inputValue.value) {
    return searchItems.value;
  }
  if (recentItems.value.length > 0) {
    return recentItems.value;
  }
  return popularItems.value;
});

const items = computed(() => sections.value.flatMap((section) => section.items));

const isMenuActive = computed(() => menuModel.value && (!inputValue.value || sections.value.length > 0));

const onOpenMenu = () => {
  menuModel.value = true;
};

const onCloseMenu = (e?: KeyboardEvent) => {
  e?.preventDefault();
  menuModel.value = false;
  menuItemIndex.value = -1;
};

const onNextMenuItem = (e: KeyboardEvent) => {
  if (!menuModel.value) {
    return;
  }

  e.preventDefault();
  if (menuItemIndex.value < items.value.length - 1) {
    menuItemIndex.value = menuItemIndex.value + 1;
  }
};

const onPreviousMenuItem = (e: KeyboardEvent) => {
  if (!menuModel.value) {
    return;
  }

  e.preventDefault();
  if (menuItemIndex.value > -1) {
    menuItemIndex.value = menuItemIndex.value - 1;
  }
};

const onChangeInput = (value: string) => {
  inputValue.value = value;
  menuModel.value = true;
  menuItemIndex.value = -1;
};

const onSubmit = (item: SearchSuggestion) => {
  getAmplitudeStore(store).setReferringComponent("site header search field");
  inputValue.value = item.query;
  // go to profile page if select a profile, otherwise go to search post page
  if (item.avatarUrl) {
    searchStore.addSearchHistory({ searchItem: item, type: "profile" });
    router.push({ name: "explore-displayName", params: { displayName: item.query } });
  } else {
    searchStore.addSearchHistory({ searchItem: item, type: "post" });
    const type = route.value.query.type || "post";
    const debug = route.value.query.debug;
    router.push({
      path: "/search",
      query: { keyword: item.query, type, ...(debug === "true" && { debug }) },
    });
  }
};

const onSubmitInput = () => {
  const item =
    menuItemIndex.value > -1 && menuItemIndex.value < items.value.length ? items.value[menuItemIndex.value] : null;
  if (menuModel.value && item) {
    consumerSearchStore.queryType = inputValue.value ? "suggested" : "recent";
    onSubmit(item);
  } else if (inputValue.value && inputValue.value.trim()) {
    consumerSearchStore.queryType = "typed";
    onSubmit({ objectID: inputValue.value, query: inputValue.value });
  }
  onCloseMenu();
};

const onClickMenuItem = (item: SearchSuggestion) => {
  consumerSearchStore.queryType = inputValue.value ? "suggested" : "recent";
  onSubmit(item);
  onCloseMenu();
};

const onUpdateMenuModel = (value: boolean) => {
  menuModel.value = value;
};

const getHighlightedState = (sectionIndex: number, itemIndex: number) => {
  let count = 0;
  for (let i = 0; i < sectionIndex; i++) {
    count += sections.value[i].items.length;
  }
  count += itemIndex;
  return count === menuItemIndex.value;
};

onMounted(() => {
  searchStore.fetchSearchHistory();
});

onClickOutside(contentRef, () => {
  onCloseMenu();
});

watchDebounced(
  inputValue,
  async (newValue) => {
    await searchAssistanceStore.fetchAutocompleteResults({ q: newValue, limit: SEARCH_SUGGESTIONS_LIMIT });
  },
  { debounce: SEARCH_SUGGESTIONS_DEBOUNCE_DELAY },
);

watch(
  () => route.value.query.keyword,
  (keywordParam) => {
    if (keywordParam === undefined || Array.isArray(keywordParam)) {
      return;
    }
    inputValue.value = keywordParam;
  },
  { immediate: true },
);

expose({
  input: inputRef,
});

return { __sfc: true,t, router, route, store, searchStore, searchAssistanceStore, consumerSearchStore, currentSuggestion, searchSuggestions, contentRef, inputRef, inputValue, menuModel, menuItemIndex, getSearchHistory, placeholderText, recentItems, popularItems, searchItems, sections, items, isMenuActive, onOpenMenu, onCloseMenu, onNextMenuItem, onPreviousMenuItem, onChangeInput, onSubmit, onSubmitInput, onClickMenuItem, onUpdateMenuModel, getHighlightedState }
}

})